import { IAction } from "../model/IAction";
import { State } from "../model/State";

import { ThunkResult } from "../../shared/utilities";
import { getTaskLabel } from "../components/TaskEditor";
import { BpmnJsCliWrapper } from "../model/BpmnJsCliWrapper";
import { initializeDefaultParameterStructure, ParameterStructure } from "../model/DefaultParametersInitializer";
import { getDefaultTriggerConfiguration } from "../model/DefaultTriggerConfigurationProvider";
import { ElementType, IParameterSchema } from "../model/IProcessData";
import { Triggers } from "../model/ITriggerData";
import { ParameterEditorValue } from "../model/ParameterEditorValue";
import { updateElementName } from "./AddOrUpdateElement";
import { updateParameter } from "./UpdateParameter";
import { rebuildVariableScopesAndTriggerValidationAction } from "./ValidateProcess";

export const UPDATE_SELECTED_TYPE = "UPDATE_SELECTED_TYPE";

export function updateSelectedTypeId(
    elementId: string | undefined, selectedTypeId: string | undefined,
    defaultSchema?: IParameterSchema[], currentParameters?: ParameterStructure, skipValidation?: boolean)
    : ThunkResult<void, State> {
    return (dispatch, getState) => {
        const defaultParameters: { [key: string]: ParameterStructure | ParameterStructure[] | ParameterEditorValue | null } = {};
        const state = getState();
        const currentElement = state.elements[elementId as string];
        if (defaultSchema) {
            for (const arg of defaultSchema) {
                const currentParams = currentParameters ? currentParameters[arg.name] : null;
                defaultParameters[arg.name] = initializeDefaultParameterStructure(arg, currentParams);
            }
        }
        dispatch<UpdateSelectedTypeAction>({
            type: UPDATE_SELECTED_TYPE,
            elementId: elementId as string,
            selectedTypeId: selectedTypeId as string,
            defaultParameters: defaultParameters,
        });

        if (currentElement.type === ElementType.Task) {
            const seletedTask = state.tasks.find((task) => task.uri === selectedTypeId);
            const oldTask = state.tasks.find((task) => task.uri === currentElement.selectedTypeId);

            if (seletedTask && (!currentElement.name || (oldTask && getTaskLabel(oldTask) === currentElement.name))) {
                dispatch(updateElementName(elementId, getTaskLabel(seletedTask)));
            }
        } else if (currentElement.type === ElementType.CallActivity) {
            const selectedSubprocess = state.subProcesses.find((subprocess) => subprocess.uniqueIdentifier === selectedTypeId);
            const oldSubprocess = state.subProcesses.find((subprocess) => subprocess.uniqueIdentifier === currentElement.selectedTypeId);

            if (selectedSubprocess && (!currentElement.name || (oldSubprocess && oldSubprocess.name === currentElement.name))) {
                dispatch(updateElementName(elementId, selectedSubprocess.name));
            }
        }
        if (!skipValidation) {
            dispatch(rebuildVariableScopesAndTriggerValidationAction());
        }
    };
}

export const UPDATE_SELECTED_TEST_TASK_TYPE = "UPDATE_SELECTED_TEST_TASK_TYPE";

export function updateSelectedTestTaskTypeId(
    elementId: string, selectedTypeId: string | undefined, defaultSchema?: IParameterSchema[])
    : ThunkResult<void, State> {
    return (dispatch) => {
        const defaultParameters: { [key: string]: ParameterStructure | ParameterStructure[] | ParameterEditorValue | null } = {};
        if (defaultSchema) {
            for (const arg of defaultSchema) {
                defaultParameters[arg.name] = initializeDefaultParameterStructure(arg, null);
            }
        }
        dispatch<UpdateSelectedTypeAction>({
            type: UPDATE_SELECTED_TEST_TASK_TYPE,
            elementId: elementId,
            selectedTypeId: selectedTypeId as string,
            defaultParameters: defaultParameters
        });
    };
}

export function updateSelectedTriggerType(
    elementId: string, selectedTypeId: string, skipValidation?: boolean)
    : ThunkResult<void, State> {
    return (dispatch, getState) => {
        const state = getState();
        const currentTriggerSettings = state.elements[elementId];
        const defaultTriggerParameters = getDefaultTriggerConfiguration(selectedTypeId, state);
        dispatch<UpdateSelectedTypeAction>({
            type: UPDATE_SELECTED_TYPE,
            elementId: elementId,
            selectedTypeId: selectedTypeId,
            defaultParameters: defaultTriggerParameters || {},
        });

        // UpdateName forces a re-render of the canvas making the correct icon appear for the trigger
        if (!currentTriggerSettings.name || Triggers[currentTriggerSettings.selectedTypeId as string].displayName === currentTriggerSettings.name) {
            dispatch(updateElementName(elementId, Triggers[selectedTypeId].displayName));
        } else {
            BpmnJsCliWrapper.fireElementChanged(elementId);
        }

        if (!skipValidation) {
            dispatch(rebuildVariableScopesAndTriggerValidationAction());
        }
    };
}

export function updateAssignVariableSetting(
    elementId: string, toggleValue: ParameterEditorValue, toggleKey: string,
    variableValue: string, variableKey: string)
    : ThunkResult<void, State> {
    return (dispatch, getState) => {
        const state = getState();
        const currentElement = state.elements[elementId];

        dispatch(updateParameter(elementId, toggleKey, toggleValue));
        dispatch(updateParameter(elementId, variableKey, variableValue));

        const oldDefaultName = getDefaultCodeShapeLabel(
            currentElement.parameters.shouldAssignVariable,
            currentElement.parameters.variableName);

        if (currentElement && (!currentElement.name || currentElement.name === oldDefaultName)) {
            dispatch(updateElementName(elementId, getDefaultCodeShapeLabel(toggleValue, variableValue)));
        }
    };
}

export interface UpdateSelectedTypeAction extends IAction {
    elementId: string;
    selectedTypeId: string;
    defaultParameters: any;
}

export function getDefaultCodeShapeLabel(shouldAssignVariable: ParameterEditorValue, variableName: string): string {
    if (shouldAssignVariable && shouldAssignVariable.value) {
        return "Assign " + variableName;
    }
    return "";
}

export function updateBypassGlobalExceptionHandler(elementId: string, value: ParameterEditorValue): ThunkResult<void, State> {
    return (dispatch) => {
        dispatch(updateParameter(elementId, "bypassGlobalExceptionHandler", value));
    };
}
